import { NavLink } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { CEW_NAV_ITEMS, CEW_LINK_TARGET } from '../../lib/cew-site';
import { DRAWER_TABS, ACTIVE_TEXT, ACTIVE_BG } from './mobile-menu-drawer-tabs';

interface DrawerIconProps {
  readonly children: React.ReactNode;
}

// Local, unexported — kept out of mobile-menu-drawer-tabs.tsx so that data
// module stays component-free (react-refresh/only-export-components).
function DrawerIcon({ children }: DrawerIconProps) {
  return (
    <svg
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="flex-none"
    >
      {children}
    </svg>
  );
}

/**
 * The two scrollable nav groups inside `MobileMenuDrawer`'s panel —
 * split out to keep that file under the 250-line cap.
 *
 *   1. "Page sections" — the tool's 6 page tabs (unchanged behavior).
 *   2. "cew.georgetown.edu" (CR-028) — a replica of `CewSiteNav`'s
 *      menu, since the desktop nav is hidden at this drawer's
 *      breakpoint. Top-level items with children nested underneath;
 *      Subscribe last (matches `CEW_NAV_ITEMS`' own order). Every
 *      link carries `target="_top"` (iframe-safe navigation,
 *      DEC-029).
 */

interface MobileMenuNavGroupsProps {
  /** Called after any nav link is tapped, closing the drawer. */
  readonly onClose: () => void;
}

export function MobileMenuNavGroups({ onClose }: MobileMenuNavGroupsProps) {
  return (
    <>
      <nav aria-label="Page sections">
        <ul>
          {DRAWER_TABS.map((tab) => (
            <li key={tab.id}>
              <NavLink
                to={tab.to}
                end
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-5 py-3.5 text-sm uppercase tracking-[0.04em]',
                    'border-b border-border-light transition',
                    ACTIVE_TEXT[tab.id],
                    isActive ? cn('font-bold', ACTIVE_BG[tab.id]) : 'font-semibold',
                  )
                }
              >
                <DrawerIcon>{tab.icon}</DrawerIcon>
                {tab.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <nav aria-label="cew.georgetown.edu" className="border-t border-border px-5 py-4">
        <ul className="space-y-3">
          {CEW_NAV_ITEMS.map((item) => (
            <li key={item.id}>
              <a
                href={item.href}
                target={CEW_LINK_TARGET}
                rel="noopener"
                className="block text-sm font-semibold text-navy"
              >
                {item.label}
              </a>
              {(item.children?.length ?? 0) > 0 && (
                <ul className="mt-2 space-y-2 pl-4">
                  {(item.children ?? []).map((child) => (
                    <li key={child.id}>
                      <a
                        href={child.href}
                        target={CEW_LINK_TARGET}
                        rel="noopener"
                        className="block text-sm text-text-3"
                      >
                        {child.label}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}
